import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { LayoutGrid, FilePlus, Settings, LogOut } from "lucide-react";
import { Logo } from "@/components/Logo";

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const navItem = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2 text-sm transition-colors rounded-md ${
      isActive
        ? "bg-[#171A20] text-white border border-[#2A2F38]"
        : "text-slate-400 hover:text-white hover:bg-[#171A20]/60 border border-transparent"
    }`;

  return (
    <div className="min-h-screen bg-[#0F1115] text-slate-200 flex">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col border-r border-[#2A2F38] bg-[#0F1115] sticky top-0 h-screen">
        <div className="px-5 py-6 border-b border-[#2A2F38]">
          <Link to="/dashboard" className="block" data-testid="sidebar-brand-link">
            <Logo size="md" />
          </Link>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          <NavLink to="/dashboard" className={navItem} data-testid="nav-dashboard-link">
            <LayoutGrid className="h-4 w-4" />
            <span>Projects</span>
          </NavLink>
          <NavLink to="/projects/new" className={navItem} data-testid="nav-new-project-link">
            <FilePlus className="h-4 w-4" />
            <span>New Estimate</span>
          </NavLink>
          <NavLink to="/settings" className={navItem} data-testid="nav-settings-link">
            <Settings className="h-4 w-4" />
            <span>Settings</span>
          </NavLink>
        </nav>

        <div className="px-3 py-4 border-t border-[#2A2F38]">
          <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-2">
            Account
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="w-full flex items-center gap-2 px-2 py-2 hover:bg-[#171A20] rounded-md transition-colors"
                data-testid="account-menu-trigger"
              >
                {user?.picture ? (
                  <img src={user.picture} alt="" className="h-7 w-7 rounded-full" />
                ) : (
                  <div className="h-7 w-7 rounded-full bg-[#FF6A00] grid place-items-center text-white text-xs font-bold">
                    {(user?.name?.[0] || user?.email?.[0] || "?").toUpperCase()}
                  </div>
                )}
                <div className="text-left flex-1 min-w-0">
                  <div className="text-xs text-white truncate">{user?.name || "User"}</div>
                  <div className="text-[10px] text-slate-500 truncate">{user?.email}</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#171A20] border-[#2A2F38] text-slate-200">
              <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-[#2A2F38]" />
              <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                <Settings className="h-4 w-4 mr-2" /> Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                <LogOut className="h-4 w-4 mr-2" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {/* Always-visible logout for accessibility */}
          <button
            onClick={handleLogout}
            className="mt-2 w-full flex items-center justify-center gap-2 text-xs text-slate-500 hover:text-red-400 hover:bg-[#171A20] rounded-md py-2 transition-colors border border-transparent hover:border-[#2A2F38]"
            data-testid="logout-btn"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-20 backdrop-blur-xl bg-[#0F1115]/80 border-b border-[#2A2F38] px-4 py-3 flex items-center justify-between">
        <Link to="/dashboard" data-testid="mobile-brand-link">
          <Logo size="sm" tagline={null} />
        </Link>
        <Button
          size="sm"
          onClick={() => navigate("/projects/new")}
          className="bg-[#FF6A00] hover:bg-[#E65C00] text-white font-semibold"
          data-testid="mobile-new-estimate-btn"
        >
          New
        </Button>
      </div>

      <main className="flex-1 md:p-8 p-4 pt-20 md:pt-8 max-w-7xl">
        {children}
      </main>
    </div>
  );
}
